/**
 * Reusable hook for copy-to-clipboard with visual feedback
 * 
 * Provides state management and handler for copying text with pulse animation
 */

import { useState, useCallback } from "react";

const COPY_PULSE_DURATION_MS = 250;

export interface UseCopyToClipboardReturn {
  /** ID of the currently copied item (for pulse animation) */
  copiedId: string | null;
  /** Copy text to clipboard and trigger pulse animation */
  copyToClipboard: (id: string, text?: string) => void;
  /** Check if a specific ID is currently showing pulse animation */
  isCopied: (id: string) => boolean;
}

/**
 * Hook for managing copy-to-clipboard state and behavior
 * 
 * @example
 * ```tsx
 * const { copiedId, copyToClipboard, isCopied } = useCopyToClipboard();
 * 
 * <span 
 *   className={isCopied(item.id) ? 'copy-pulse' : ''}
 *   onClick={() => copyToClipboard(item.id, item.value)}
 * >
 *   {item.value}
 * </span>
 * ```
 */
export function useCopyToClipboard(): UseCopyToClipboardReturn {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = useCallback((id: string, text?: string) => {
    const textToCopy = text || id;
    navigator.clipboard.writeText(textToCopy);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), COPY_PULSE_DURATION_MS);
  }, []);

  const isCopied = useCallback((id: string) => copiedId === id, [copiedId]);

  return {
    copiedId,
    copyToClipboard,
    isCopied,
  };
}

